import React, { useEffect, useState, forwardRef, createRef, useRef } from "react";
import {
  SafeAreaView,
  ScrollView,
  View,
  StyleSheet,
  Platform,
  KeyboardAvoidingView,
} from "react-native";
import { Grid, Container, Row, Column } from "ui-grid";
import { MoreScreenProps } from "../../navigation/more-stack/more-stack";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { translate } from "../../i18n";
import { yupResolver } from "@hookform/resolvers/yup";
import { changePasswordSchema } from "../home/schemas";
import { InputAccessoryNavigation } from "../../components";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { AuthService } from "../../api/AuthService";
import { PasswordFields, CognitoErrors } from "../../enums";
import automationTestingLocators from "../../screens/new/utils/automation-testing-locators.json";
import { Auth } from "aws-amplify";
import { useNavigation } from "@react-navigation/native";
import {
  theme,
  Button,
  ITextInput,
  TextInput,
  OnScreenAlert,
  ErrorWarningBottomSheet,
} from "ui-components";

const { CURRENT_PASSWORD, NEW_PASSWORD, CONFIRM_NEW_PASSWORD } = PasswordFields;

enum ErrorWarningBottomSheetType {
  error = "error",
  warning = "warning",
}

type ErrorWarningBottomSheetTypes = `${ErrorWarningBottomSheetType}`;

interface ErrorMsgObjectProps {
  title?: string;
  body?: string;
  errorType?: ErrorWarningBottomSheetTypes;
}

export const ChangePassword = ({ route }: MoreScreenProps) => {
  const styles = getStyles();
  const [isLoading, setLoading] = useState(false);
  const errorPopupRef = useRef<any>();
  const [errorMsgObject, setErrorMsgObject] = useState<ErrorMsgObjectProps>({});
  const inputAccessoryViewID = "inputAccessoryViewID1";
  const textInputRef = [createRef(), createRef(), createRef()];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const logger = useSelector((state: RootState) => state.logger);
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const authService = new AuthService(cognitoUser);
  const navigation = useNavigation();

  const {
    register,
    handleSubmit,
    getValues,
    setValue: originalSetValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(changePasswordSchema),
  });

  useEffect(() => {
    register(CURRENT_PASSWORD);
    register(NEW_PASSWORD);
    register(CONFIRM_NEW_PASSWORD);
  }, [register]);

  const showGenericError = () => {
    setErrorMsgObject({
      title: translate("changePassword.unexpectedErrorTitle") as string,
      body: translate("changePassword.unexpectedErrorBody") as string,
      errorType: "error",
    });
    errorPopupRef?.current?.open();
  };

  const showInvalidPasswordError = () => {
    setErrorMsgObject({
      title: translate("changePassword.incorrectPasswordTitle") as string,
      body: translate("changePassword.incorrectPasswordBody") as string,
      errorType: "error",
    });
    errorPopupRef?.current?.open();
  };

  const showWeakPasswordError = () => {
    setErrorMsgObject({
      title: translate("changePassword.weakPasswordTitle") as string,
      body: translate("changePassword.weakPasswordBody") as string,
      errorType: "error",
    });
    errorPopupRef?.current?.open();
  };

  const setValue = (formName: string, val: any) => {
    clearErrors(formName);
    originalSetValue(formName, val);
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmNewPassword } = getValues();
    try {
      setLoading(true);
      const resp = await authService.validatePassword(cognitoUser, newPassword);
      if (!resp.validated) {
        showWeakPasswordError();
        return;
      }
      const success = await Auth.changePassword(cognitoUser, currentPassword, newPassword);
      if (success) {
        navigation.goBack();
      } else {
        showGenericError();
      }
      setLoading(false);
    } catch (error) {
      const { NOT_AUTHORIZED, INVALID_PARAMETER } = CognitoErrors;
      const { code } = error;
      switch (code) {
        case NOT_AUTHORIZED:
        case INVALID_PARAMETER:
          showInvalidPasswordError();
          break;
        default:
          //Only log error if unknown
          logger.error(error);
          showGenericError();
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={Platform.OS === "web" ? undefined : 155}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollableArea}>
          <View style={{ marginTop: 15 }} />
          <View>
            <Grid>
              <Container>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.header}>
                    <View style={{ margin: 6 }} />
                    <OnScreenAlert
                      alertType="caution"
                      titleStye={{
                        fontFamily: "Lato_700Bold",
                        fontSize: 17,
                        marginBottom: 10,
                      }}
                      title={translate("changePassword.passWordRequirementsHeader") as string}
                      body={
                        `\u2022  ${translate("changePassword.passWordRequirements1") as string}\n` +
                        `\u2022  ${
                          translate("changePassword.passWordRequirements2") as string
                        } \n` +
                        `\u2022  ${translate("changePassword.passWordRequirements3") as string}`
                      }
                      bodyStyle={{
                        color: theme.colors.text,
                        fontSize: 14,
                      }}
                      containerStyle={styles.onScreanAlertContainer}
                      iconColor={theme.colors.text}
                      titleColor={theme.colors.text}
                      {...automationTestingLocators.changePassowrd.requirementsOnScreenAlert}
                    />
                    <View style={{ margin: 6 }} />
                  </Column>
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={{ margin: 6 }} />
                    <LongRow
                      value={getValues(CURRENT_PASSWORD)}
                      styles={styles}
                      errors={errors}
                      setValue={setValue}
                      formName={CURRENT_PASSWORD}
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={textInputRef[0] as any}
                      onFocus={() => setCurrentInputIndex(0)}
                      onSubmitEditing={() => (textInputRef[1].current as any).focus()}
                      returnKeyType="next"
                      placeholder=""
                      rightIconColor="#F26924"
                      secureTextEntry={true}
                      {...automationTestingLocators.changePassowrd.currentPassowrdInput}
                    />

                    <View style={{ margin: 6 }} />
                  </Column>
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={{ margin: 6 }} />
                    <LongRow
                      value={getValues(NEW_PASSWORD)}
                      styles={styles}
                      errors={errors}
                      setValue={setValue}
                      formName={NEW_PASSWORD}
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={textInputRef[1] as any}
                      onFocus={() => setCurrentInputIndex(1)}
                      onSubmitEditing={() => (textInputRef[2].current as any).focus()}
                      returnKeyType="next"
                      placeholder=""
                      secureTextEntry={true}
                      rightIconColor="#F26924"
                      {...automationTestingLocators.changePassowrd.newPassowrdInput}
                    />
                    <View style={{ margin: 6 }} />
                  </Column>
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={{ margin: 6 }} />
                    <LongRow
                      value={getValues(CONFIRM_NEW_PASSWORD)}
                      styles={styles}
                      errors={errors}
                      setValue={setValue}
                      formName={CONFIRM_NEW_PASSWORD}
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={textInputRef[2] as any}
                      onFocus={() => setCurrentInputIndex(2)}
                      onSubmitEditing={() => {}}
                      secureTextEntry={true}
                      rightIconColor="#F26924"
                      returnKeyType="next"
                      placeholder=""
                      {...automationTestingLocators.changePassowrd.confirmNewPassowrdInput}
                    />
                    <View style={{ margin: 6 }} />
                  </Column>
                </Row>
              </Container>
            </Grid>
          </View>
        </ScrollView>
        <BottomNav backgroundColor={theme.colors.white}>
          <View style={{ marginTop: 15 }} />
          <Row>
            <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
            <Column span={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <View style={{ paddingVertical: 5 }}>
                <Button
                  type={"primary"}
                  text={translate("changePassword.saveChanges") as string}
                  loading={isLoading}
                  disabled={isLoading}
                  onPress={handleSubmit(handleChangePassword)}
                  {...automationTestingLocators.changePassowrd.saveChangesButton}
                />
              </View>
            </Column>
          </Row>
          {Platform.OS === "ios" ? (
            <InputAccessoryNavigation
              inputAccessoryViewID={inputAccessoryViewID}
              ref={textInputRef as any}
              currentInputIndex={currentInputIndex}
            />
          ) : null}
        </BottomNav>
        <Footer />
        <ErrorWarningBottomSheet
          ref={errorPopupRef}
          title={errorMsgObject?.title}
          body={errorMsgObject?.body}
          height={190}
          alertType={errorMsgObject?.errorType}
          showButtons={true}
          showLeftButton={false}
          rightButtonText={translate("changePassword.errorButtonConfirmation") as string}
          onPressRightButton={() => errorPopupRef?.current?.close()}
          {...automationTestingLocators.changePassowrd.errorWarningAlert}
        />
      </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

interface LongRowProps extends ITextInput {
  styles: any;
  setValue: any;
  formName: string;
  errors?: Record<string, any>;
  rightIcon?: any;
  secureTextEntry?: boolean;
}

const LongRow = forwardRef(
  (
    {
      styles,
      setValue,
      formName,
      placeholder,
      errors,
      render,
      maxLength,
      keyboardType = "default",
      returnKeyType,
      inputAccessoryViewID,
      onFocus,
      onSubmitEditing,
      value,
      showInfoIcon,
      tooltipTitle,
      tooltipBody,
      rightIcon,
      secureTextEntry,
      ...props
    }: LongRowProps,
    ref: any
  ) => {
    return (
      <View accessible={true}>
        <TextInput
          value={value}
          labelStyle={{ fontFamily: "Lato_700Bold" }}
          label={translate(`changePassword.${formName}`)}
          error={errors?.[formName]}
          mode="outlined"
          render={render}
          keyboardType={keyboardType}
          maxLength={maxLength}
          placeholder={placeholder}
          onChangeText={(text) => setValue(formName, text)}
          ref={ref}
          returnKeyType={returnKeyType}
          inputAccessoryViewID={inputAccessoryViewID}
          onFocus={onFocus}
          onSubmitEditing={onSubmitEditing}
          showInfoIcon={showInfoIcon}
          tooltipTitle={tooltipTitle}
          tooltipBody={tooltipBody}
          tooltipWebWidth={325}
          rightIcon={rightIcon}
          secureTextEntry={secureTextEntry}
          {...props}
        />
      </View>
    );
  }
);

const getStyles = () => {
  return StyleSheet.create({
    container: {
      flex: 1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: "#FFFFFF",
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: "#FFFFFF",
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: "#FFFFFF",
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
    },
    scrollView: {
      flexGrow: 0,
    },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      }),
    },
    onScreanAlertContainer: {
      backgroundColor: theme.colors.lightInfo,
      borderWidth: 0,
    },
    header: {
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingVertical: 3 },
        webTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
        },
        webDesktop: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
        },
        iosPhone: { paddingVertical: 3 },
        iosTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
        },
        androidPhone: { paddingVertical: 3 },
        androidTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
        },
      }),
    },
    instructionImageContainer: {
      borderWidth: 1,
      padding: 8,
      borderRadius: 10,
      borderColor: theme.colors.stroke,
      height: 64,
      width: 64,
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
      }),
    },
    bottomNav: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {},
        webDesktop: {},
        iosPhone: { flex: 1 },
        iosTablet: {},
        androidPhone: { flex: 1 },
        androidTablet: {},
      }),
    },
  });
};

export default ChangePassword;
